import {getToken} from '../utils/auth';
import {fetchPhotoBlob} from './warehousePhotos';
import {API_BASE} from 'src/api/endpoints';

/**
 * Вложения обращения в поддержку (SKS-124) — теми же обычными HTTP-эндпоинтами,
 * что вложения чата: multipart через Apollo Server 5 не проходит, а метаданные
 * приезжают в ленте обычным GraphQL-запросом.
 *
 * Основное вложение здесь — скриншот, поэтому обычный выбор файла: требование
 * «только камера приложения» (ТЗ п. 1) относится к фото-доказательствам приёмки.
 */

/**
 * Относительный адрес тела вложения — в этом виде его ждут PhotoImage и photoCache
 * (они сами добавляют базу и заголовок Authorization).
 */
export function supportAttachmentPath(messageId: string): string {
	return `/api/support-tickets/attachments/${messageId}`;
}

/**
 * Обращение сразу с файлом — одним запросом (SKS-124).
 *
 * Так текст становится подписью к вложению и в ленте появляется один пузырь,
 * как в чате. Две отправки (мутация + загрузка) давали два сообщения на одно
 * действие человека: сначала текст, потом отдельно картинка.
 */
export function createSupportTicketWithFile(params: {
	companyId: string;
	body: string;
	idempotencyKey: string;
	subjectType?: string;
	subjectId?: string;
	/** Ветка чата, из которой позвали поддержку (SKS-291) — как у мутации. */
	chatSubjectType?: string;
	chatSubjectId?: string;
	originPath?: string;
	file: File;
	onProgress?: (percent: number) => void;
}): Promise<{id: string; number: string}> {
	const {file, onProgress, ...fields} = params;

	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();
		const form = new FormData();

		form.append('file', file);

		// Поля обращения — тем же multipart-запросом: их разбирает тот же DTO,
		// что и у мутации, поэтому проверки предмета и длин совпадают
		for (const [key, value] of Object.entries(fields)) {
			// Сравнение с undefined, а не проверка на пустоту: пустой текст —
			// это «скриншот без слов», и сервер его принимает (CreateSupportTicketInput)
			if (value !== undefined) {
				form.append(key, value);
			}
		}

		request.open('POST', `${API_BASE}/api/support-tickets`);

		const token = getToken();

		if (token) {
			request.setRequestHeader('Authorization', token);
		}

		request.upload.addEventListener('progress', (event) => {
			if (event.lengthComputable) {
				onProgress?.(Math.round((event.loaded / event.total) * 100));
			}
		});

		request.addEventListener('load', () => {
			if (request.status >= 200 && request.status < 300) {
				try {
					resolve(JSON.parse(request.responseText) as {id: string; number: string});
				} catch {
					reject(new Error('Обращение создано, но ответ не разобрать'));
				}
			} else {
				reject(new Error(errorMessage(request.responseText, request.status)));
			}
		});

		request.addEventListener('error', () => reject(new Error('Не удалось отправить обращение')));

		request.send(form);
	});
}

/**
 * Отправка файла с прогрессом. XMLHttpRequest, а не fetch: fetch не сообщает
 * прогресс отправки, а скриншот с мобильной сети идёт заметное время.
 */
export function uploadSupportAttachment(params: {
	ticketId: string;
	file: File;
	caption?: string;
	/** Ключ попытки отправки: ретрай после обрыва не создаст второе сообщение. */
	idempotencyKey: string;
	onProgress?: (percent: number) => void;
}): Promise<void> {
	const {ticketId, file, caption, idempotencyKey, onProgress} = params;

	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();
		const body = new FormData();

		body.append('file', file);

		// Подпись — полем формы: в строке запроса длинный текст перевешивал лимит
		// заголовков Node, и сообщение падало на второй тысяче знаков
		if (caption) {
			body.append('caption', caption);
		}

		const query = new URLSearchParams({idempotencyKey});

		request.open('POST', `${API_BASE}/api/support-tickets/${ticketId}/attachments?${query}`);

		const token = getToken();

		if (token) {
			request.setRequestHeader('Authorization', token);
		}

		request.upload.addEventListener('progress', (event) => {
			if (event.lengthComputable) {
				onProgress?.(Math.round((event.loaded / event.total) * 100));
			}
		});

		request.addEventListener('load', () => {
			if (request.status >= 200 && request.status < 300) {
				resolve();
			} else {
				reject(new Error(errorMessage(request.responseText, request.status)));
			}
		});

		request.addEventListener('error', () => reject(new Error('Не удалось отправить файл')));

		request.send(body);
	});
}

/**
 * Скачивание вложения кликом — через blob: эндпоинт закрыт заголовком
 * Authorization, которого обычная ссылка не отправит (тот же разбор —
 * в api/warehousePhotos).
 */
export async function downloadSupportAttachment(
	messageId: string,
	fileName: string,
): Promise<void> {
	const blob = await fetchPhotoBlob(supportAttachmentPath(messageId));
	const objectUrl = URL.createObjectURL(blob);

	try {
		const link = document.createElement('a');

		link.href = objectUrl;
		link.download = fileName;
		link.click();
	} finally {
		// Отзыв не сразу: щелчок по ссылке отрабатывает асинхронно, и мгновенный
		// revoke в части браузеров обрывает начатое скачивание
		setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
	}
}

/** Бэкенд отвечает обычным Nest-ответом об ошибке — достаём из него текст. */
function errorMessage(body: string, status: number): string {
	try {
		const parsed = JSON.parse(body) as {message?: string | string[]};

		// Валидатор Nest отдаёт массив: без разбора в интерфейс попадал бы
		// весь список, склеенный запятыми
		if (Array.isArray(parsed.message)) {
			return parsed.message[0] ?? 'Не удалось отправить файл';
		}

		if (parsed.message) {
			return parsed.message;
		}
	} catch {
		// Не JSON — покажем общий текст ниже
	}

	return status === 413 ? 'Файл слишком большой' : 'Не удалось отправить файл';
}
